"use client";

import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Code, Database, Server, Wrench, WandSparkles, Sparkles } from "lucide-react";
import Image from "next/image";
import { LiquidGlassCard } from "@/components/ui/liquid-glass-card";
import { usePortfolio } from "@/components/portfolio-provider";

const iconMap: Record<string, React.ReactNode> = {
  Code: <Code className="h-6 w-6 text-primary" />,
  Server: <Server className="h-6 w-6 text-primary" />,
  Database: <Database className="h-6 w-6 text-primary" />,
  WandSparkles: <WandSparkles className="h-6 w-6 text-primary" />,
  Wrench: <Wrench className="h-6 w-6 text-primary" />,
};

type SkillCategory = {
  title: string;
  icon: React.ReactNode;
  accentColor: string;
  skills: Array<{ name: string; icon: string }>;
};

const PREFERRED_ORDER = [
  "Frontend",
  "Backend",
  "Database",
  "Tools & Workflow",
  "DevOps & Cloud",
  "Integrations",
];

const defaultSkillCategories: SkillCategory[] = [
  {
    title: "Frontend",
    icon: <Code className="h-6 w-6 text-primary" />,
    accentColor: "#c9a84c",
    skills: [
      { name: "JavaScript", icon: "/icons/js.webp" },
      { name: "TypeScript", icon: "/icons/ts.webp" },
      { name: "React.js", icon: "/icons/react.webp" },
      { name: "Next.js", icon: "/icons/nextjs.webp" },
      { name: "Redux", icon: "/icons/redux.webp" },
      { name: "Tailwind CSS", icon: "/icons/tailwind.webp" },
      { name: "ShadCN UI", icon: "/icons/shadcn.webp" },
      { name: "Ant Design", icon: "/icons/antd.webp" },
    ],
  },
  {
    title: "Backend",
    icon: <Server className="h-6 w-6 text-primary" />,
    accentColor: "#d9bb67",
    skills: [
      { name: "Nest.js", icon: "/icons/nest.webp" },
      { name: "Node.js", icon: "/icons/nodejs.webp" },
      { name: "Express.js", icon: "/icons/express.webp" },
      { name: "REST API", icon: "/icons/rest.webp" },
    ],
  },
  {
    title: "Database",
    icon: <Database className="h-6 w-6 text-primary" />,
    accentColor: "#f0d89a",
    skills: [
      { name: "PostgreSQL", icon: "/icons/postgresql.webp" },
      { name: "Prisma ORM", icon: "/icons/prisma.webp" },
      { name: "TypeORM", icon: "/icons/typeorm.webp" },
      { name: "Mongoose ODM", icon: "/icons/mongoose.webp" },
    ],
  },
  {
    title: "Tools & Workflow",
    icon: <Wrench className="h-6 w-6 text-primary" />,
    accentColor: "#d7bf80",
    skills: [
      { name: "Postman", icon: "/icons/postman.webp" },
      { name: "GitHub", icon: "/icons/github.webp" },
      { name: "Git", icon: "/icons/git.webp" },
    ],
  },
  {
    title: "DevOps & Cloud",
    icon: <Server className="h-6 w-6 text-primary" />,
    accentColor: "#e8d4a0",
    skills: [
      { name: "DigitalOcean", icon: "/icons/digitalocean.webp" },
      { name: "Linux", icon: "/icons/linux.webp" },
      { name: "GitHub Actions", icon: "/icons/git.webp" },
      { name: "Cloudflare R2", icon: "/icons/cloudflare.webp" },
    ],
  },
  {
    title: "Integrations",
    icon: <WandSparkles className="h-6 w-6 text-primary" />,
    accentColor: "#ceb777",
    skills: [
      { name: "Stripe", icon: "/icons/stripe.webp" },
      { name: "SSLCommerz", icon: "/icons/sslcommerz.webp" },
      { name: "Google Calendar API", icon: "/icons/google-calendar.webp" },
      { name: "OpenAI API", icon: "/icons/openai.webp" },
    ],
  },
];

function SkillCategoryCard({
  category,
  index,
}: {
  category: SkillCategory;
  index: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-30px" }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.25) }}
      className="w-full"
    >
      <LiquidGlassCard
        className="p-6 rounded-3xl"
        spotlightColor="rgba(201, 168, 76, 0.22)"
        enableTilt
      >
        {/* Category Header */}
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/15 border border-primary/25 shadow-[inset_0_1px_1px_rgba(255,255,255,0.25)]">
              {category.icon}
            </div>
            <div>
              <h3 className="text-lg font-bold text-foreground tracking-tight">
                {category.title}
              </h3>
              <p className="text-xs text-muted-foreground font-medium">
                {category.skills.length} core technologies
              </p>
            </div>
          </div>
          <div className="h-2 w-2 rounded-full bg-primary/80 shadow-[0_0_8px_rgba(201,168,76,0.6)]" />
        </div>

        {/* Skills Chips */}
        <div className="flex flex-wrap gap-2.5">
          {category.skills.map((skill) => (
            <div
              key={skill.name}
              className="group/item transition-transform duration-200 hover:-translate-y-0.5 hover:scale-[1.02]"
            >
              <div className="flex cursor-default items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 transition-all duration-200 shadow-[inset_0_1px_0.5px_rgba(255,255,255,0.15)] hover:border-primary/45 hover:bg-primary/15 hover:shadow-[0_4px_16px_rgba(201,168,76,0.15)]">
                <div className="relative shrink-0">
                  <Image
                    src={skill.icon}
                    width={20}
                    height={20}
                    alt={skill.name}
                    className="size-5 rounded object-cover"
                  />
                </div>
                <span className="text-xs font-semibold text-foreground/85 transition-colors group-hover/item:text-foreground">
                  {skill.name}
                </span>
              </div>
            </div>
          ))}
        </div>
      </LiquidGlassCard>
    </motion.div>
  );
}

export default function Skills() {
  const { data } = usePortfolio();
  const dbCategories =
    data?.skillCategories && data.skillCategories.length > 0
      ? data.skillCategories
      : null;

  const rawCategories: SkillCategory[] = dbCategories
    ? dbCategories.map((cat, idx) => ({
        title: cat.name,
        icon:
          iconMap[cat.iconName] ||
          [
            <Code key="c" className="h-6 w-6 text-primary" />,
            <Server key="s" className="h-6 w-6 text-primary" />,
            <Database key="d" className="h-6 w-6 text-primary" />,
            <Wrench key="wr" className="h-6 w-6 text-primary" />,
            <Server key="s2" className="h-6 w-6 text-primary" />,
            <WandSparkles key="w" className="h-6 w-6 text-primary" />,
          ][idx % 6],
        accentColor: cat.accentColor || "#c9a84c",
        skills: cat.skills.map((s) => ({
          name: s.name,
          icon: s.iconUrl,
        })),
      }))
    : defaultSkillCategories;

  // Sort categories by preferred serial order:
  // 1: Frontend, 2: Backend, 3: Database, 4: Tools & Workflow, 5: DevOps & Cloud, 6: Integrations
  const sortedCategories = [...rawCategories].sort((a, b) => {
    const indexA = PREFERRED_ORDER.findIndex(
      (name) => name.toLowerCase() === a.title.toLowerCase()
    );
    const indexB = PREFERRED_ORDER.findIndex(
      (name) => name.toLowerCase() === b.title.toLowerCase()
    );
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return 0;
  });

  // Masonry 3-Column Distribution (Desktop):
  // Col 1: Frontend (0), Tools & Workflow (3)
  // Col 2: Backend (1), DevOps & Cloud (4)
  // Col 3: Database (2), Integrations (5)
  const desktopCol1 = sortedCategories.filter((_, i) => i % 3 === 0);
  const desktopCol2 = sortedCategories.filter((_, i) => i % 3 === 1);
  const desktopCol3 = sortedCategories.filter((_, i) => i % 3 === 2);

  // Masonry 2-Column Distribution (Tablet):
  // Col 1: Frontend (0), Database (2), DevOps & Cloud (4)
  // Col 2: Backend (1), Tools & Workflow (3), Integrations (5)
  const tabletCol1 = sortedCategories.filter((_, i) => i % 2 === 0);
  const tabletCol2 = sortedCategories.filter((_, i) => i % 2 === 1);

  return (
    <section id="skills" className="section-shell relative overflow-x-clip py-10 sm:py-14 md:py-18 lg:py-20">
      {/* Background Liquid Ambient Light */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute right-1/4 top-1/4 h-72 w-72 rounded-full bg-[radial-gradient(circle,rgba(201,168,76,0.12)_0%,transparent_70%)]" />
        <div className="absolute -left-10 bottom-1/4 h-64 w-64 rounded-full bg-[radial-gradient(circle,rgba(201,168,76,0.08)_0%,transparent_70%)]" />
      </div>

      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 sm:mb-10"
        >
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-4 py-1.5 backdrop-blur-xl shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)]">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            <span className="text-xs font-semibold uppercase tracking-wider text-primary">
              Core Stack
            </span>
          </div>
          <h2 className="section-title mb-4 font-bold">
            Technical <span className="accent-gradient-text">Proficiencies</span>
          </h2>
          <p className="section-subtitle mx-auto max-w-2xl text-muted-foreground">
            A comprehensive toolset engineered for building modern, reactive,
            and enterprise-grade applications with maximum performance.
          </p>
        </motion.div>

        {/* Desktop 3-Column Masonry System (xl: >= 1280px)
            Column 1: Frontend (top), Tools & Workflow (bottom)
            Column 2: Backend (top), DevOps & Cloud (bottom)
            Column 3: Database (top), Integrations (bottom) */}
        <div className="hidden xl:grid xl:grid-cols-3 gap-6 items-start">
          <div className="flex flex-col gap-6">
            {desktopCol1.map((cat, idx) => (
              <SkillCategoryCard
                key={cat.title}
                category={cat}
                index={idx * 3}
              />
            ))}
          </div>
          <div className="flex flex-col gap-6">
            {desktopCol2.map((cat, idx) => (
              <SkillCategoryCard
                key={cat.title}
                category={cat}
                index={idx * 3 + 1}
              />
            ))}
          </div>
          <div className="flex flex-col gap-6">
            {desktopCol3.map((cat, idx) => (
              <SkillCategoryCard
                key={cat.title}
                category={cat}
                index={idx * 3 + 2}
              />
            ))}
          </div>
        </div>

        {/* Tablet 2-Column Masonry System (md: 768px - 1279px) */}
        <div className="hidden md:grid xl:hidden md:grid-cols-2 gap-6 items-start">
          <div className="flex flex-col gap-6">
            {tabletCol1.map((cat, idx) => (
              <SkillCategoryCard
                key={cat.title}
                category={cat}
                index={idx * 2}
              />
            ))}
          </div>
          <div className="flex flex-col gap-6">
            {tabletCol2.map((cat, idx) => (
              <SkillCategoryCard
                key={cat.title}
                category={cat}
                index={idx * 2 + 1}
              />
            ))}
          </div>
        </div>

        {/* Mobile 1-Column Sequential Layout (< 768px) */}
        <div className="grid md:hidden grid-cols-1 gap-6">
          {sortedCategories.map((category, index) => (
            <SkillCategoryCard
              key={category.title}
              category={category}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
